function defineCustomElement(name, constructor, options) {
  if (!customElements.get(name)) {
    customElements.define(name, constructor, options);
  }
}

class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.addEventListener('cart:refresh', this.onCartRefreshListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.removeEventListener('cart:refresh', this.onCartRefreshListener);
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.dataset.productAddition === 'pending') this.setAttribute('active', '');

    if (this.open && !Shopify.designMode) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }
  }

  beginProductAddition() {
    const token = {};
    this.productAdditionToken = token;
    this.setProductAdditionState('pending', document.documentElement.lang.startsWith('en') ? 'Adding to cart…' : 'Ajout en cours…');
    return token;
  }

  setProductAdditionState(state, message, requiresReview = false) {
    const content = this.querySelector('.drawer__content');
    if (!content) return;
    this.dataset.productAddition = state;
    this.productAdditionRequiresReview = requiresReview;
    this.toggleAttribute('aria-busy', state === 'pending');
    if (state === 'pending') this.setAttribute('aria-busy', 'true');
    let status = content.querySelector('[data-cart-addition-status]');
    if (!status) {
      status = document.createElement('div');
      status.className = 'cart-drawer__addition-status';
      status.dataset.cartAdditionStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      content.querySelector('.drawer__header')?.after(status);
    }
    status.replaceChildren();
    const label = document.createElement('p');
    label.textContent = message;
    status.append(label);
    if (state !== 'pending') {
      const action = document.createElement(requiresReview ? 'a' : 'button');
      action.className = 'btn btn--primary';
      action.textContent = document.documentElement.lang.startsWith('en')
        ? requiresReview ? 'Check my cart' : 'Back to product'
        : requiresReview ? 'Vérifier mon panier' : 'Revenir au produit';
      if (requiresReview) action.href = FoxTheme.routes.cart_url;
      else {
        action.type = 'button';
        action.addEventListener('click', () => this.hide());
      }
      status.append(action);
    }
    // Preserve the current shell and focus controls while concealing old totals
    // and checkout actions until the new cart is confirmed.
    content.querySelectorAll(':scope > :not(.drawer__header):not([data-cart-addition-status])').forEach((element) => {
      if (element.hasAttribute('data-cart-addition-blocked')) return;
      element.dataset.cartAdditionBlocked = element.inert ? 'inert' : '';
      element.inert = true;
    });
  }

  finishProductAddition(token, { error = '', requiresReview = false } = {}) {
    if (token !== this.productAdditionToken) return;
    if (error && (this.open || requiresReview)) {
      this.setProductAdditionState('error', error, requiresReview);
      return;
    }
    this.productAdditionToken = null;
    this.productAdditionRequiresReview = false;
    delete this.dataset.productAddition;
    this.removeAttribute('aria-busy');
    this.querySelector('[data-cart-addition-status]')?.remove();
    this.querySelectorAll('[data-cart-addition-blocked]').forEach((element) => {
      element.inert = element.dataset.cartAdditionBlocked === 'inert';
      delete element.dataset.cartAdditionBlocked;
    });
  }

  hide() {
    const token = this.productAdditionToken;
    const clearError = this.dataset.productAddition === 'error' && !this.productAdditionRequiresReview;
    const hidden = super.hide();
    if (clearError) Promise.resolve(hidden).then(() => {
      if (!this.open) this.finishProductAddition(token);
    });
    return hidden;
  }

  async onCartRefresh(event) {
    try {
      // Fetch both HTML and JSON data in parallel for better performance
      const [htmlResponse, cartResponse] = await Promise.all([
        fetch(`${FoxTheme.routes.root_url}?section_id=${this.sectionId}`),
        fetch(`${FoxTheme.routes.cart_url}.js`),
      ]);

      if (!htmlResponse.ok || !cartResponse.ok) {
        throw new Error('Failed to fetch cart data');
      }

      const [responseText, cartData] = await Promise.all([htmlResponse.text(), cartResponse.json()]);

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, 'text/html');
      const sectionToRender = parsedHTML.getElementById(`CartDrawer-${this.sectionId}`);

      if (!sectionToRender) {
        console.warn('Cart drawer section not found in response');
        return;
      }

      // Add sections data to cartData for pubsub system
      cartData.sections = {};
      cartData.sections[this.sectionId] = sectionToRender.innerHTML;

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        cart: cartData,
      });

      if (event.detail && event.detail.open === true) {
        this.show();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
}
defineCustomElement('cart-drawer', CartDrawer);

class CartAddonModal extends ModalComponent {
  constructor() {
    super();
  }

  /**
   * To avoid lost focus input elements
   */
  get focusElement() {
    return this.querySelector('input, textarea, select') || this.querySelector('button');
  }
}
defineCustomElement('cart-addon-modal', CartAddonModal);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.formEl = this.closest('form');
    this.submitBtnEl = document.querySelector(`button[form="${this.formEl?.id}"]`);
    this.submitBtnEl && this.submitBtnEl.addEventListener('click', this.onCartSubmit.bind(this));
    this.quotePricingRefreshFrame = null;

    this.debouncedOnChange = FoxTheme.utils.debounce(this.onChange.bind(this), 300);
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.closest('quantity-input')) {
        this.debouncedOnChange(event);
      }
    });
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartItemProducts = this.querySelectorAll('.cart-item__product');
    this.cartItemQuantitys = this.querySelectorAll('.cart-item__quantity');

    const loadTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const template = element.querySelector('template');
        const templateContent = template && document.importNode(template.content, true);
        const parent = element.querySelector(parentSelector);

        if (parent && template && !parent.querySelector('.template-content')) {
          const contentWrapper = document.createElement('div');
          contentWrapper.classList.add('template-content');
          contentWrapper.appendChild(templateContent);
          parent.appendChild(contentWrapper);
        }
      });
    };

    const removeTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const parent = element.querySelector(parentSelector);
        const templateContent = parent?.querySelector('.template-content');

        if (templateContent) {
          parent.removeChild(templateContent);
        }
      });
    };

    const handleTabletMatch = () => {
      loadTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      removeTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const handleTabletUnmatch = () => {
      removeTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      loadTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
    FoxTheme.config.mqlTablet = mqlTablet.matches;

    if (FoxTheme.config.mqlTablet) {
      handleTabletMatch();
    } else {
      handleTabletUnmatch();
    }

    mqlTablet.onchange = (event) => {
      if (event.matches) {
        handleTabletMatch();
      } else {
        FoxTheme.config.mqlTablet = false;
        handleTabletUnmatch();
      }
    };

    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
    this.initializeQuoteQuantityControls();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.queueQuotePricingRefresh();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    if (this.quotePricingRefreshFrame != null) {
      window.cancelAnimationFrame(this.quotePricingRefreshFrame);
      this.quotePricingRefreshFrame = null;
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement?.getAttribute('name') || event.target.getAttribute('name'),
      event.target
    );
  }

  onCartError(errors, target) {
    if (target) {
      this.hideLoader(target.getAttribute('data-index'));
      this.setValidity(target, errors);
      return;
    } else {
      window.location.href = FoxTheme.routes.cart_url;
    }
    alert(errors);
  }

  onCartSubmit(event) {
    this.clearValidity(this.formEl);

    if (!this.formEl.checkValidity()) {
      return;
    }
  }

  getLineItemElement(line) {
    return document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
  }

  getLineKey(line) {
    return this.getLineItemElement(line)?.getAttribute('data-line-key') || null;
  }

  getTargetRules(target) {
    if (window.TodayMerchQuantityPricing && typeof window.TodayMerchQuantityPricing.getRulesFromAttributes === 'function') {
      return window.TodayMerchQuantityPricing.getRulesFromAttributes(target, {
        min: target?.getAttribute('data-min') || target?.getAttribute('min') || 1,
        increment: target?.getAttribute('step') || 1,
        max: target?.getAttribute('max') || null,
      });
    }

    return {
      min: target?.getAttribute('data-min') || target?.getAttribute('min') || 1,
      increment: target?.getAttribute('step') || 1,
      max: target?.getAttribute('max') || null,
    };
  }

  normalizeCartQuantity(quantity, target) {
    const parsedQuantity = Number.parseInt(quantity, 10) || 0;
    if (parsedQuantity <= 0) return 0;

    if (!window.TodayMerchQuantityPricing) {
      return parsedQuantity;
    }

    return window.TodayMerchQuantityPricing.normalizeQuantity(parsedQuantity, this.getTargetRules(target), 'up');
  }

  getLineProperties(line) {
    const lineItem = this.getLineItemElement(line);
    return this.getElementLineProperties(lineItem);
  }

  isSupplierPricingProperties(properties) {
    if (window.TodayMerchQuantityPricing?.isSupplierPricing) return window.TodayMerchQuantityPricing.isSupplierPricing(properties);
    return properties?._fee_mode === 'separate' || properties?._pricing_mode === 'supplier_quote' || properties?._pricing_mode === 'personalization_quote' || properties?._pricing_mode === 'quote_required'
      || Object.keys(properties || {}).some((key) => key.startsWith('_xd_') || key.startsWith('_personalization_'));
  }

  getSeparateFeePropertyKeys() {
    return ['_fee_mode', '_items_total_price_cents', '_technical_fees_cents', '_preparation_fees_cents', '_preparation_unit_price_micro', '_total_price_cents'];
  }

  supplierInteger(value, { allowZero = false } = {}) {
    if (typeof value !== 'number' && (typeof value !== 'string' || !/^(?:0|[1-9]\d*)$/.test(value))) return null;
    const parsed = Number(value);
    if (!Number.isSafeInteger(parsed)) return null;
    return allowZero ? parsed >= 0 ? parsed : null : parsed > 0 ? parsed : null;
  }

  readSeparateSupplierFees(properties, quantity, { allowExpiredSnapshot = false } = {}) {
    if (!properties || properties._fee_mode !== 'separate') return null;
    const normalizedQuantity = this.supplierInteger(quantity);
    const unitPriceCents = this.supplierInteger(properties._applied_unit_price_cents);
    const itemsTotalPriceCents = this.supplierInteger(properties._items_total_price_cents);
    const technicalFeesCents = this.supplierInteger(properties._technical_fees_cents, { allowZero: true });
    const preparationFeesCents = this.supplierInteger(properties._preparation_fees_cents, { allowZero: true });
    const totalPriceCents = this.supplierInteger(properties._total_price_cents);
    const personalized = properties._personalization_pricing === '1' || properties._pricing_mode === 'personalization_quote';
    const estimate = !personalized && properties._pricing_mode === 'supplier_estimate' && properties._xd_estimated === '1';
    const hasPriority = Object.entries(properties).some(([key, value]) => (key.startsWith('_tm_priority_') || key === '_tm_production_priority') && value != null && value !== '');
    if (estimate && hasPriority) {
      const baseUnit = this.supplierInteger(properties._tm_priority_base_unit_price_cents);
      const baseItems = this.supplierInteger(properties._tm_priority_base_items_total_cents);
      const baseTechnical = this.supplierInteger(properties._tm_priority_base_technical_fees_cents, { allowZero: true });
      const basePreparation = this.supplierInteger(properties._tm_priority_base_preparation_fees_cents, { allowZero: true });
      const baseTotal = this.supplierInteger(properties._tm_priority_base_total_cents);
      const priority = properties._tm_production_priority;
      const uplift = value => priority === 'express' ? Math.round(value * 1.1) : value;
      const priorityValid = properties._tm_priority_version === '2' && ['standard', 'express'].includes(priority)
        && properties._tm_priority_rate_bps === (priority === 'express' ? '1000' : '0')
        && baseUnit !== null && baseItems === baseUnit * normalizedQuantity && baseTechnical !== null && basePreparation !== null
        && baseTotal !== null && baseTotal === baseItems + baseTechnical + basePreparation
        && unitPriceCents === uplift(baseUnit) && technicalFeesCents === uplift(baseTechnical) && preparationFeesCents === uplift(basePreparation)
        && this.supplierInteger(properties._tm_priority_surcharge_cents, { allowZero: true }) === totalPriceCents - baseTotal
        && this.supplierInteger(properties._tm_priority_total_cents) === totalPriceCents;
      if (!priorityValid) return { invalid: true };
    }
    const quoteMode = personalized ? 'personalization_quote' : estimate ? 'supplier_estimate' : 'supplier_quote';
    const quotedQuantity = this.supplierInteger(personalized ? properties._personalization_quote_quantity : estimate ? properties._xd_estimate_quantity : properties._xd_quote_quantity);
    const validUntil = personalized ? properties._personalization_quote_valid_until : estimate ? properties._xd_estimate_valid_until : properties._xd_quote_valid_until;
    const validUntilMs = typeof validUntil === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(validUntil)
      ? Date.parse(validUntil) : NaN;
    const valid = properties._pricing_mode === quoteMode && properties._pricing_currency === 'EUR'
      && (!personalized || properties._personalization_quote_only === '1')
      && normalizedQuantity !== null && quotedQuantity === normalizedQuantity
      && unitPriceCents !== null && itemsTotalPriceCents !== null
      && technicalFeesCents !== null && preparationFeesCents !== null && totalPriceCents !== null
      && itemsTotalPriceCents === unitPriceCents * normalizedQuantity
      && totalPriceCents === itemsTotalPriceCents + technicalFeesCents + preparationFeesCents
      && Number.isFinite(validUntilMs) && (validUntilMs > Date.now() || allowExpiredSnapshot);
    return valid ? { unitPriceCents, itemsTotalPriceCents, technicalFeesCents, preparationFeesCents, totalPriceCents,
      ...(validUntilMs <= Date.now() ? { displayOnly: true, requiresRequote: true, validUntil } : {}) } : { invalid: true };
  }

  convertSupplierPricing(pricing, components, quantity) {
    if (!components) return pricing;
    let unitPriceCents = components.unitPriceCents;
    let itemsTotalPriceCents = components.itemsTotalPriceCents;
    let technicalFeesCents = components.technicalFeesCents;
    let preparationFeesCents = components.preparationFeesCents;
    if (window.TodayMerchMoney) {
      unitPriceCents = window.TodayMerchMoney.convert(unitPriceCents, 'EUR');
      itemsTotalPriceCents = unitPriceCents === null ? null : unitPriceCents * quantity;
      technicalFeesCents = window.TodayMerchMoney.convert(technicalFeesCents, 'EUR');
      preparationFeesCents = window.TodayMerchMoney.convert(preparationFeesCents, 'EUR');
    }
    if (![unitPriceCents, itemsTotalPriceCents, technicalFeesCents, preparationFeesCents].every(Number.isSafeInteger)) return null;
    return {
      ...(pricing || {}),
      valid: true,
      pricingMode: pricing?.pricingMode || 'supplier_quote',
      currency: window.TodayMerchMoney?.activeCurrency?.() || pricing?.currency || window.FoxTheme?.settings?.moneyCurrency || window.Shopify?.currency?.active || 'EUR',
      sourceCurrency: 'EUR',
      unitPriceCents,
      itemsTotalPriceCents,
      technicalFeesCents,
      preparationFeesCents,
      linePriceCents: itemsTotalPriceCents + technicalFeesCents + preparationFeesCents,
      sourceUnitPriceCents: components.unitPriceCents,
      sourceItemsTotalPriceCents: components.itemsTotalPriceCents,
      sourceTechnicalFeesCents: components.technicalFeesCents,
      sourcePreparationFeesCents: components.preparationFeesCents,
      sourceLinePriceCents: components.totalPriceCents,
      feeMode: 'separate',
    };
  }

  getSupplierDisplayPricing(properties, quantity, identity = {}, { allowExpiredSnapshot = false } = {}) {
    const helperPricing = window.TodayMerchQuantityPricing?.getDisplayPricing?.({ product_id: identity.productId, variant_id: identity.variantId, properties: properties || { _pricing_mode: 'quote_required' }, quantity }, { allowExpiredSnapshot });
    if (properties?._pricing_mode === 'supplier_estimate' && (helperPricing?.valid !== true || helperPricing.pricingMode !== 'supplier_estimate')) return null;
    const components = this.readSeparateSupplierFees(properties, quantity, { allowExpiredSnapshot });
    if (components?.invalid) return null;
    if (components?.displayOnly && helperPricing?.displayOnly !== true) return null;
    if (components && helperPricing && helperPricing.valid === false) return null;
    if (components) return this.convertSupplierPricing(helperPricing, components, quantity);
    return helperPricing;
  }

  clearSeparateFeeProperties(properties) {
    if (!properties) return properties;
    this.getSeparateFeePropertyKeys().forEach((key) => { properties[key] = ''; });
    return properties;
  }

  getElementLineProperties(lineItem) {
    const node = lineItem?.querySelector('[data-cart-line-properties]');
    if (!node) return null;
    try {
      const serialized = String(node.textContent || '{}').trim();
      const parsed = JSON.parse(serialized);
      let properties = parsed;
      if (Array.isArray(parsed)) {
        const keys = new Set();
        const validEntries = parsed.every((entry) => {
          if (!Array.isArray(entry) || entry.length !== 2 || typeof entry[0] !== 'string' || !entry[0]
            || keys.has(entry[0]) || (entry[1] !== null && !['string', 'number', 'boolean'].includes(typeof entry[1]))) return false;
          keys.add(entry[0]);
          return true;
        });
        if (!validEntries) return null;
        properties = Object.fromEntries(parsed);
      } else if (!parsed || typeof parsed !== 'object') {
        return null;
      }
      if (this.isSupplierPricingProperties(properties)) return properties;
      if (lineItem.dataset.quotePricingNative === 'true') properties._pricing_native = 'true';
      const activeCurrency = window.TodayMerchMoney?.activeCurrency();
      const storedCurrency = properties._pricing_currency || window.TodayMerchMoney?.baseCurrency();
      if (activeCurrency && storedCurrency !== activeCurrency && lineItem.dataset.currentPricingCurrency === activeCurrency && this.parseQuotePricingTiers(lineItem.dataset.currentPricingTiers).length) {
        properties._pricing_tiers = lineItem.dataset.currentPricingTiers;
        properties._pricing_currency = activeCurrency;
      }
      return properties;
    } catch (error) {
      console.warn('Unable to parse line item properties', error);
      return null;
    }
  }

  getQuoteControlLine(control) {
    const explicitLine = Number.parseInt(control?.dataset.line, 10);
    if (Number.isFinite(explicitLine) && explicitLine > 0) return explicitLine;

    const fallbackInput = control?.querySelector('[data-quote-cart-qty-input], [data-quote-cart-qty-fallback] [data-index]');
    const inputLine = Number.parseInt(fallbackInput?.dataset.index, 10);
    if (Number.isFinite(inputLine) && inputLine > 0) return inputLine;

    const cartItem = control?.closest('.quote-cart-item, .cart-item');
    const cartItemId = cartItem?.id || '';
    const matchedLine = cartItemId.match(/-(\d+)$/);
    if (!matchedLine) return null;

    const cartLine = Number.parseInt(matchedLine[1], 10);
    return Number.isFinite(cartLine) && cartLine > 0 ? cartLine : null;
  }

  getQuotePricingTiers(control) {
    if (control?.dataset.pricingNative === 'true') return [];
    const line = this.getQuoteControlLine(control);
    const properties = line ? this.getLineProperties(line) : null;
    if (this.isSupplierPricingProperties(properties)) return [];
    const datasetTiers = control?.dataset.pricingTiers || '';
    const parsedDatasetTiers = this.parseQuotePricingTiers(datasetTiers);
    if (parsedDatasetTiers.length) {
      return parsedDatasetTiers;
    }

    const propertyTiers = properties?._pricing_tiers || '';
    const parsedPropertyTiers = this.parseQuotePricingTiers(propertyTiers);

    if (parsedPropertyTiers.length && typeof propertyTiers === 'string') {
      control.dataset.pricingTiers = propertyTiers;
    }

    return parsedPropertyTiers;
  }

  buildUpdatedPricingProperties(line, quantity, target) {
    if (!window.TodayMerchQuantityPricing || quantity <= 0) return null;

    const existingProperties = this.getLineProperties(line);
    if (!existingProperties || (!existingProperties._pricing_tiers && !this.isSupplierPricingProperties(existingProperties))) return null;

    const updatedProperties = window.TodayMerchQuantityPricing.buildUpdatedProperties(existingProperties, quantity, this.getTargetRules(target));
    if (updatedProperties && this.isSupplierPricingProperties(existingProperties) && updatedProperties._pricing_mode === 'quote_required') {
      this.clearSeparateFeeProperties(updatedProperties);
    }
    return updatedProperties;
  }

  parseQuotePricingTiers(serialized) {
    if (!serialized) return [];

    if (window.TodayMerchQuantityPricing && typeof window.TodayMerchQuantityPricing.parseTiers === 'function') {
      return window.TodayMerchQuantityPricing.parseTiers(serialized);
    }

    try {
      const parsed = JSON.parse(serialized);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  formatQuoteMoney(cents) {
    if (window.TodayMerchQuantityPricing && typeof window.TodayMerchQuantityPricing.formatMoney === 'function') {
      return window.TodayMerchQuantityPricing.formatMoney(cents, FoxTheme?.settings?.moneyFormat);
    }

    return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
  }

  computeQuotePricingState(quantity, tiers, rules) {
    if (!Array.isArray(tiers) || !tiers.length) return null;

    if (window.TodayMerchQuantityPricing && typeof window.TodayMerchQuantityPricing.computeState === 'function') {
      return window.TodayMerchQuantityPricing.computeState({
        quantity,
        tiers,
        rules,
        quantityMode: 'up',
      });
    }

    const min = Math.max(1, Number.parseInt(rules?.min, 10) || 1);
    const step = Math.max(1, Number.parseInt(rules?.step || rules?.increment, 10) || 1);
    const max = rules?.max == null || rules?.max === '' ? null : Number.parseInt(rules.max, 10);

    let normalizedQuantity = Number.parseInt(quantity, 10);
    if (!Number.isFinite(normalizedQuantity) || normalizedQuantity < min) {
      normalizedQuantity = min;
    }

    if (step > 1) {
      normalizedQuantity = min + Math.ceil((normalizedQuantity - min) / step) * step;
    }

    if (Number.isFinite(max) && normalizedQuantity > max) {
      normalizedQuantity = max;
      if (step > 1 && normalizedQuantity > min) {
        normalizedQuantity = min + Math.floor((normalizedQuantity - min) / step) * step;
      }
    }

    let appliedTier = tiers[0];
    tiers.forEach((tier) => {
      if (Number(tier.qty) <= normalizedQuantity) appliedTier = tier;
    });

    return {
      quantity: normalizedQuantity,
      appliedTierQty: Number(appliedTier.qty),
      unitPriceCents: Number(appliedTier.unit_price_cents),
      totalCents: normalizedQuantity * Number(appliedTier.unit_price_cents),
      pricingMode: normalizedQuantity === Number(appliedTier.qty) ? 'tier' : 'custom',
    };
  }

  getQuoteCurrentQuantity(control) {
    const input = control?.querySelector('[data-quote-cart-qty-input]');
    const lineItem = control?.closest('.quote-cart-item, .cart-item');
    const rawValue = input?.value || control?.dataset.currentQuantity || lineItem?.dataset.quantity;
    const parsed = Number.parseInt(rawValue, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : Number.parseInt(input?.getAttribute('data-min'), 10) || 1;
  }

  getQuoteTierOptions(control, target) {
    const rules = this.getTargetRules(target || control?.querySelector('[data-quote-cart-qty-input]'));
    const tiers = this.getQuotePricingTiers(control)
      .map((tier) => ({
        qty: Number.parseInt(tier?.qty, 10),
        unit_price_cents: Number.parseInt(tier?.unit_price_cents ?? tier?.unitPriceCents, 10),
      }))
      .filter((tier, index, values) => {
        if (!Number.isFinite(tier.qty) || tier.qty <= 0 || !Number.isFinite(tier.unit_price_cents) || tier.unit_price_cents <= 0) {
          return false;
        }

        if (tier.qty < Number(rules.min || 1)) return false;
        if (rules.max != null && tier.qty > Number(rules.max)) return false;

        return values.findIndex((candidate) => candidate.qty === tier.qty) === index;
      })
      .sort((tierA, tierB) => tierA.qty - tierB.qty);

    return { rules, tiers };
  }

  renderQuoteQuantityControlState(control, state) {
    if (!control || !state) return;

    const customInput = control.querySelector('[data-quote-cart-qty-input]');
    if (customInput && document.activeElement !== customInput) {
      customInput.value = String(state.quantity);
      customInput.defaultValue = String(state.quantity);
    }
    control.dataset.currentQuantity = String(state.quantity);
  }

  getQuoteFallbackLineTotal(lineItem) {
    const rawCents = Number.parseInt(lineItem?.dataset.quoteLineTotalCents, 10);
    return Number.isFinite(rawCents) && rawCents >= 0 ? rawCents : null;
  }

  setQuoteLinePrice(lineItem, cents) {
    const priceNode = lineItem?.querySelector('[data-quote-cart-line-price]');
    if (!priceNode || !Number.isFinite(cents) || cents < 0) return;

    priceNode.textContent = this.formatQuoteMoney(cents);
    lineItem.dataset.quoteLineTotalCents = String(cents);
    if (window.TodayMerchMoney) lineItem.dataset.quoteSourceCurrency = window.TodayMerchMoney.activeCurrency();
  }

  setQuoteUnitPrice(lineItem, cents) {
    const unitPriceNode = lineItem?.querySelector('[data-quote-cart-unit-price]');
    if (!unitPriceNode || !Number.isFinite(cents) || cents < 0) return;

    unitPriceNode.textContent = this.formatQuoteMoney(cents);
  }

  getQuoteSummaryTotalElement() {
    const scopedSummaryTotal =
      this.closest('.drawer__content')?.querySelector('[data-quote-cart-summary-total]') ||
      this.closest('.cart')?.querySelector('[data-quote-cart-summary-total]');
    if (scopedSummaryTotal) return scopedSummaryTotal;

    const sectionId = FoxTheme.utils.getSectionId(this);
    return (
      document.querySelector(`#CartDrawerFooter-${sectionId} [data-quote-cart-summary-total]`) ||
      document.querySelector(`[data-quote-cart-summary-total]`)
    );
  }

  getQuoteFeeSummaryElements() {
    const scope = this.closest?.('.drawer__content') || this.closest?.('.cart') || document;
    return {
      items: scope.querySelector?.('[data-quote-cart-items-total]') || null,
      technical: scope.querySelector?.('[data-quote-cart-technical-fees]') || null,
      preparation: scope.querySelector?.('[data-quote-cart-preparation-fees]') || null,
      itemsRow: scope.querySelector?.('[data-quote-cart-items-row]') || null,
      technicalRow: scope.querySelector?.('[data-quote-cart-technical-fees-row]') || null,
      preparationRow: scope.querySelector?.('[data-quote-cart-preparation-fees-row]') || null,
    };
  }

  setQuoteFeeSummary({ itemsTotalCents = 0, technicalFeesCents = 0, preparationFeesCents = 0, valid = true, unavailableLabel = 'Sur devis' } = {}) {
    const elements = this.getQuoteFeeSummaryElements();
    if (!elements.items && !elements.technical && !elements.preparation) return;
    if (elements.itemsRow) elements.itemsRow.hidden = !valid;
    if (elements.technicalRow) elements.technicalRow.hidden = !valid || technicalFeesCents <= 0;
    if (elements.preparationRow) elements.preparationRow.hidden = !valid || preparationFeesCents <= 0;
    if (!valid) {
      [elements.items, elements.technical, elements.preparation].forEach((node) => { if (node) node.textContent = unavailableLabel; });
      return;
    }
    if (elements.items) elements.items.textContent = this.formatQuoteMoney(itemsTotalCents);
    if (elements.technical) elements.technical.textContent = this.formatQuoteMoney(technicalFeesCents);
    if (elements.preparation) elements.preparation.textContent = this.formatQuoteMoney(preparationFeesCents);
  }

  queueQuotePricingRefresh() {
    if (this.quotePricingRefreshFrame != null) {
      window.cancelAnimationFrame(this.quotePricingRefreshFrame);
    }

    this.quotePricingRefreshFrame = window.requestAnimationFrame(() => {
      this.quotePricingRefreshFrame = null;
      this.refreshQuotePricingDisplay();
    });
  }

  refreshQuotePricingDisplay() {
    const lineItems = Array.from(this.querySelectorAll('.quote-cart-item'));
    if (!lineItems.length) return;

    let totalCents = 0;
    let itemsTotalCents = 0;
    let technicalFeesCents = 0;
    let preparationFeesCents = 0;
    let validTotal = true;
    let supplierPriceUnavailable = false;
    const supplierUnpricedLabel = String(document.documentElement.lang || window.Shopify?.locale || '').startsWith('fr') ? 'À confirmer' : 'To be confirmed';

    lineItems.forEach((lineItem) => {
      const control = lineItem.querySelector('[data-quote-cart-qty-control]');
      const properties = this.getElementLineProperties(lineItem);
      if (this.isSupplierPricingProperties(properties) || lineItem.dataset.quotePricingStatus === 'quote_required' || lineItem.dataset.quoteFeeMode === 'separate') {
        const supplierQuantity = control ? this.getQuoteCurrentQuantity(control) : Number(lineItem.dataset.quantity);
        const supplierProductId = lineItem.querySelector('[data-product-id]')?.dataset.productId;
        const supplierVariantId = lineItem.dataset.variantId;
        const supplierPricing = this.getSupplierDisplayPricing(properties, supplierQuantity, { productId: supplierProductId, variantId: supplierVariantId }, { allowExpiredSnapshot: true });
        const separateFees = supplierPricing?.feeMode === 'separate';
        const supplierLineTotal = separateFees ? supplierPricing.itemsTotalPriceCents : supplierPricing?.linePriceCents;
        const supplierFullTotal = supplierPricing?.linePriceCents;
        const scopedPriority = supplierProductId === '15411282837890' && supplierVariantId === '56962772337026'
          && supplierPricing?.priorityApplied === true && !separateFees;
        const unitPriceValid = scopedPriority
          ? Number.isFinite(supplierPricing?.unitPriceCents) && supplierPricing.unitPriceCents > 0
          : Number.isSafeInteger(supplierPricing?.unitPriceCents) && supplierPricing.unitPriceCents > 0;
        const lineTotalValid = separateFees
          ? Number.isSafeInteger(supplierLineTotal) && supplierLineTotal > 0 && Number.isSafeInteger(supplierFullTotal) && supplierFullTotal > 0
          : scopedPriority
            ? Number.isSafeInteger(supplierFullTotal) && supplierFullTotal > 0 && Math.abs(supplierFullTotal - supplierPricing.unitPriceCents * supplierQuantity) < 0.000001
            : Number.isSafeInteger(supplierFullTotal) && supplierFullTotal > 0 && supplierFullTotal === supplierPricing.unitPriceCents * supplierQuantity;
        const supplierValid = supplierPricing && supplierPricing.valid === true && ['supplier_quote', 'supplier_estimate', 'personalization_quote'].includes(supplierPricing.pricingMode)
          && unitPriceValid && lineTotalValid;
        if (!supplierValid) {
          validTotal = false;
          supplierPriceUnavailable = true;
          lineItem.dataset.quoteLineTotalCents = '';
          lineItem.dataset.quoteItemsTotalCents = '';
          lineItem.dataset.quoteTotalPriceCents = '';
          lineItem.dataset.quoteTechnicalFeesCents = '';
          lineItem.dataset.quotePreparationFeesCents = '';
          lineItem.dataset.quotePricingStatus = 'quote_required';
          lineItem.querySelectorAll('[data-quote-cart-line-price], [data-quote-cart-unit-price]').forEach((node) => { node.textContent = supplierUnpricedLabel; });
          return;
        }
        lineItem.dataset.quotePricingStatus = supplierPricing.displayOnly ? 'display_snapshot' : 'priced';
        lineItem.dataset.quoteItemsTotalCents = String(supplierLineTotal);
        lineItem.dataset.quoteTotalPriceCents = String(supplierFullTotal);
        lineItem.dataset.quoteTechnicalFeesCents = String(supplierPricing.technicalFeesCents || 0);
        lineItem.dataset.quotePreparationFeesCents = String(supplierPricing.preparationFeesCents || 0);
        lineItem.dataset.quoteFeeMode = supplierPricing.feeMode || '';
        // This inclusive unit is presentation-only; preserve the quote components above.
        const displayUnitCents = separateFees ? Math.ceil(supplierFullTotal / supplierQuantity) : supplierPricing.unitPriceCents;
        this.setQuoteUnitPrice(lineItem, displayUnitCents);
        this.setQuoteLinePrice(lineItem, supplierFullTotal);
        totalCents += supplierFullTotal;
        itemsTotalCents += supplierLineTotal;
        technicalFeesCents += supplierPricing.technicalFeesCents || 0;
        preparationFeesCents += supplierPricing.preparationFeesCents || 0;
        return;
      }
      let lineTotalCents = this.getQuoteFallbackLineTotal(lineItem);
      let unitPriceCents = null;
      let sourceCurrency = lineItem.dataset.quoteSourceCurrency;
      let quantity = Number(lineItem.dataset.quantity || 1);

      if (control) {
        const target = control.querySelector('[data-quote-cart-qty-input]');
        const { rules, tiers } = this.getQuoteTierOptions(control, target);

        if (tiers.length) {
          const state = this.computeQuotePricingState(this.getQuoteCurrentQuantity(control), tiers, rules);
          if (state) {
            lineTotalCents = state.totalCents;
            unitPriceCents = state.unitPriceCents;
            quantity = state.quantity;
            sourceCurrency = control.dataset.pricingCurrency || this.getLineProperties(this.getQuoteControlLine(control))?._pricing_currency || window.TodayMerchMoney?.baseCurrency();
            this.renderQuoteQuantityControlState(control, state);
          }
        }
      }

      if (window.TodayMerchMoney) {
        if (unitPriceCents !== null) {
          unitPriceCents = window.TodayMerchMoney.convert(unitPriceCents, sourceCurrency);
          lineTotalCents = unitPriceCents === null ? null : unitPriceCents * quantity;
        } else {
          lineTotalCents = window.TodayMerchMoney.convert(lineTotalCents, sourceCurrency, quantity);
        }
      } else if (sourceCurrency && sourceCurrency !== (window.FoxTheme?.settings?.moneyCurrency || window.Shopify?.currency?.active || sourceCurrency)) {
        lineTotalCents = null;
      }
      if (!Number.isFinite(lineTotalCents) || lineTotalCents < 0) {
        validTotal = false;
        lineItem.querySelectorAll('[data-quote-cart-line-price], [data-quote-cart-unit-price]').forEach((node) => { node.textContent = '—'; });
        return;
      }

      if (Number.isFinite(unitPriceCents) && unitPriceCents >= 0) {
        this.setQuoteUnitPrice(lineItem, unitPriceCents);
      }
      this.setQuoteLinePrice(lineItem, lineTotalCents);
      totalCents += lineTotalCents;
      itemsTotalCents += lineTotalCents;
    });

    const summaryTotal = this.getQuoteSummaryTotalElement();
    if (summaryTotal && totalCents >= 0) {
      summaryTotal.textContent = validTotal ? this.formatQuoteMoney(totalCents) : supplierPriceUnavailable ? supplierUnpricedLabel : '—';
    }
    this.setQuoteFeeSummary({
      itemsTotalCents,
      technicalFeesCents,
      preparationFeesCents,
      valid: validTotal,
      unavailableLabel: supplierPriceUnavailable ? supplierUnpricedLabel : '—',
    });
  }

  initializeQuoteQuantityControls() {
    this.querySelectorAll('[data-quote-cart-qty-control]').forEach((control) => {
      this.setupQuoteQuantityControl(control);
    });

    this.refreshQuotePricingDisplay();
    this.queueQuotePricingRefresh();
  }

  setupQuoteQuantityControl(control) {
    const customInput = control.querySelector('[data-quote-cart-qty-input]');

    if (!customInput) return;

    const currentQuantity = this.getQuoteCurrentQuantity(control);
    const { rules, tiers } = this.getQuoteTierOptions(control, customInput);
    const currentState = this.computeQuotePricingState(currentQuantity, tiers, rules);
    customInput.value = String(currentQuantity);
    customInput.defaultValue = String(currentQuantity);
    control.dataset.currentQuantity = String(currentQuantity);

    if (currentState) {
      this.renderQuoteQuantityControlState(control, currentState);
    }
  }

  clearValidity(form) {
    form.querySelectorAll('input, select, textarea').forEach((el) => {
      el.setCustomValidity('');
    });
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    if (typeof target.select === 'function') {
      target.select();
    } else if (typeof target.focus === 'function') {
      target.focus();
    }
  }

  onCartUpdate(event) {
    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target);
      return;
    }
    if (typeof event.cart.sections === 'undefined') {
      return;
    }

    const sectionId = FoxTheme.utils.getSectionId(this);
    const sectionToRender = new DOMParser().parseFromString(event.cart.sections[sectionId], 'text/html');

    const cartDrawer = document.querySelector(`#CartDrawer-${sectionId}`);
    const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
    const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
    const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);
    if (cartDrawer) {
      const cartDrawerBodyUpdate = sectionToRender.querySelector(`#CartDrawerBody-${sectionId}`);
      const cartDrawerFooterUpdate = sectionToRender.querySelector(`#CartDrawerFooter-${sectionId}`);
      const cartDrawerEmptyUpdate = sectionToRender.querySelector(`#CartDrawerEmpty-${sectionId}`);

      if (cartDrawerBodyUpdate) {
        cartDrawerBody.innerHTML = cartDrawerBodyUpdate.innerHTML;
      }
      if (cartDrawerFooterUpdate) {
        cartDrawerFooter.innerHTML = cartDrawerFooterUpdate.innerHTML;
      }

      if (cartDrawerEmptyUpdate) {
        cartDrawerEmpty.innerHTML = cartDrawerEmptyUpdate.innerHTML;
      }

      if (event.cart.item_count > 0) {
        cartDrawerBody.classList.remove('hidden');
        cartDrawerFooter.classList.remove('hidden');
        cartDrawerEmpty.classList.add('hidden');
      } else {
        cartDrawerBody.classList.add('hidden');
        cartDrawerFooter.classList.add('hidden');
        cartDrawerEmpty.classList.remove('hidden');
      }
    }

    const mainCart = document.querySelector(`#MainCart-${sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${sectionId}`);
      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
        if (event.cart.item_count > 0) {
          mainCart.closest('.cart').classList.remove('is-empty');
        } else {
          mainCart.closest('.cart').classList.add('is-empty');
        }
      } else {
        mainCart.closest('.cart').classList.add('is-empty');
        mainCart.remove();
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);

    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      FoxTheme.a11y.trapFocus(mainCart || cartDrawer, lineItem.querySelector(`[name="${event.name}"]`));
    } else if (event.cart.item_count === 0) {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('a'))
        : FoxTheme.a11y.trapFocus(document.querySelector('.cart__empty'), document.querySelector('a'));
    } else {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__title'))
        : FoxTheme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart-item__title'));
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: event.cart,
        },
      })
    );
  }

  updateQuantity(line, quantity, name, target) {
    this.showLoader(line);

    const normalizedQuantity = this.normalizeCartQuantity(quantity, target);
    if (target && String(target.value) !== String(normalizedQuantity)) {
      target.value = normalizedQuantity;
    }

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const lineKey = this.getLineKey(line);
    const payload = {
      quantity: normalizedQuantity,
      sections: sectionsToBundle,
    };

    const parsedLine = Number.parseInt(line, 10);
    if (Number.isFinite(parsedLine) && parsedLine > 0) {
      payload.line = parsedLine;
    } else if (lineKey) {
      payload.id = lineKey;
    }

    const updatedProperties = this.buildUpdatedPricingProperties(line, normalizedQuantity, target);
    if (updatedProperties) {
      payload.properties = updatedProperties;
    }

    const body = JSON.stringify(payload);

    fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: parsedState,
          target,
          line,
          name,
          quantity: normalizedQuantity,
          lineKey,
        });
      })
      .catch((error) => {
        this.hideLoader(line);
        console.error(error);
      });
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    const lineItem = this.getLineItemElement(line);
    lineItem?.classList.add('is-loading');
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add('btn--loading');
      });
    }
  }

  hideLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    const lineItem = this.getLineItemElement(line);
    lineItem?.classList.remove('is-loading');
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.remove('btn--loading');
      });
    }
  }
}
defineCustomElement('cart-items', CartItems);

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems = this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
defineCustomElement('cart-remove-item', CartRemoveItem, { extends: 'a' });

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));

    if (this.button && this.cartNoteDetailsSummary) {
      this.button.addEventListener('click', () => {
        this.cartNoteDetailsSummary.close();
      });
    }
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } });
  }
}
defineCustomElement('cart-note', CartNote);

class CartDiscount extends HTMLFormElement {
  constructor() {
    super();

    this.addEventListener('submit', this.handleFormSubmit.bind(this));
  }

  get submitEl() {
    return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
  }

  get messageEl() {
    return (this._messageEl = this._messageEl || this.querySelector('.form__message'));
  }

  get couponEl() {
    return (this._couponEl = this._couponEl || this.querySelector('input[name="discount"]'));
  }

  get cartAddonDrawer() {
    return this.closest('.cart-addons-drawer');
  }

  get cartDiscountsEl() {
    return document.querySelector('.cart__discounts');
  }

  getDiscounts() {
    const discounts = [];

    if (this.cartDiscountsEl) {
      const items = this.cartDiscountsEl.querySelectorAll('.discount');
      items &&
        items.forEach((item) => {
          discounts.push(item.dataset.discountCode);
        });
    }

    return discounts;
  }

  handleFormSubmit(event) {
    event.preventDefault();

    if (this.submitEl.getAttribute('aria-disabled') === 'true') return;

    this.displayFormErrors();

    const newDiscountCode = this.couponEl.value;
    const discounts = this.getDiscounts();

    if (discounts.includes(newDiscountCode)) {
      this.displayFormErrors(FoxTheme.cartStrings.duplicateDiscountError);
      return;
    }

    discounts.push(newDiscountCode);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', discounts.join(','));

    config.body = formData;

    this.submitEl.setAttribute('aria-disabled', 'true');
    this.submitEl.classList.add('btn--loading');

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (
          parsedState.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === newDiscountCode && discount.applicable === false;
          })
        ) {
          this.couponEl.value = '';
          this.displayFormErrors(FoxTheme.cartStrings.applyDiscountError);
          return;
        }

        if (this.cartAddonDrawer) {
          this.cartAddonDrawer.hide();
        }

        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };

  displayFormErrors = (errorMessage = false) => {
    if (!this.messageEl) {
      if (errorMessage !== false) {
        alert(errorMessage);
      }
    } else {
      this.messageEl.classList.toggle('!hidden', !errorMessage);
      if (errorMessage !== false) {
        this.messageEl.innerText = errorMessage;
      }
    }
  };
}
defineCustomElement('cart-discount', CartDiscount, { extends: 'form' });

class CartDiscountRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.selectors = {
      list: '.cart__discounts',
      item: '.discount',
    };

    this.clickHandler = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.listEl = this.closest(this.selectors.list);

    this.addEventListener('click', this.clickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.clickHandler);
  }

  handleClick(event) {
    event.preventDefault();
    if (this.getAttribute('aria-disabled') === 'true') return;

    this.setAttribute('aria-disabled', 'true');
    this.classList.add('btn--loading');

    this.discounts = [];

    const thisItem = this.closest('li');
    const items = this.listEl.querySelectorAll(this.selectors.item);
    items &&
      items.forEach((item) => {
        if (item != thisItem) {
          this.discounts.push(item.dataset.discountCode);
        }
      });

    this.updateCartDiscounts();
  }

  updateCartDiscounts() {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', this.discounts.join(','));

    config.body = formData;

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };
}
defineCustomElement('cart-discount-remove', CartDiscountRemove, { extends: 'button' });

class CalculateShipping extends CartAddonModal {
  constructor() {
    super();
    this.countryProvince = this.querySelector('country-province');
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, 'data-show'];
  }
  show() {
    super.show();
    this.setAttribute('data-show', true);
  }

  hide() {
    super.hide();
    this.setAttribute('data-show', false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'data-show' && newValue === 'true' && !this.isCountrySetup && this.countryProvince) {
      const template = this.countryProvince.querySelector('template');
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
defineCustomElement('calculate-shipping', CalculateShipping);
class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));
    if (this.template && this.template === 'cart') {
      this.init();
    }
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.dataset.country)
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
defineCustomElement('country-province', CountryProvinceForm);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.classList.add('btn--loading');

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove('btn--loading');
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error blocks-radius grid gap-2">
        <p class="font-body-bolder m-0">${FoxTheme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert blocks-radius alert--${
        shippingRates.length === 0 ? 'error' : 'success'
      } grid gap-2 leading-tight">
        <p class="font-body-bolder m-0">${
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults
        }</p>
        ${
          shippingRatesList === ''
            ? ''
            : `<ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>`
        }
      </div>
    `;
  }
}
defineCustomElement('shipping-calculator', ShippingCalculator, { extends: 'form' });

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();
    this.carousel = false;
    this.isLoading = false;

    this.classes = {
      grid: 'f-grid',
      swiper: 'swiper',
      swiperWrapper: 'swiper-wrapper',
      loading: 'is-loading',
    };

    // Handle Safari separately
    const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
    if (isSafari) {
      this.init();
    } else {
      FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
  }

  disconnectedCallback() {
    // Cleanup
    this.destroyCarousel();
    const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
    mql.removeEventListener('change', this.handleCarousel.bind(this));
  }

  init() {
    if (this.isLoading || !this.dataset.url) return;

    this.isLoading = true;
    this.classList.add(this.classes.loading);

    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (!sectionInnerHTML) return;

        const recommendations = sectionInnerHTML.querySelector('cart-drawer-products-recommendation');
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
          mql.addEventListener('change', this.handleCarousel.bind(this));
          this.handleCarousel();
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        } else {
          this.closest('.shopify-section')?.remove();
        }
      })
      .catch((error) => {
        console.error('Error loading recommendations:', error);
        this.classList.add('has-error');
      })
      .finally(() => {
        this.isLoading = false;
        this.classList.remove(this.classes.loading);
      });
  }

  initCarousel() {
    if (!this.wrapper?.innerHTML.trim().length) {
      this.classList.add('hidden');
      return;
    }

    if (typeof this.carousel !== 'object') {
      try {
        this.container.classList.add(this.classes.swiper);
        this.wrapper.classList.remove(this.classes.grid);
        this.wrapper.classList.add(this.classes.swiperWrapper);

        let options = {
          slidesPerView: this.columns,
          spaceBetween: 8,
          loop: false,
          navigation: {
            nextEl: this.sliderNext,
            prevEl: this.sliderPrev,
          },
        };

        const paginationEl = this.querySelector('.swiper-pagination');
        if (paginationEl) {
          options = {
            ...options,
            pagination: {
              el: paginationEl,
              clickable: true,
              type: 'fraction',
            },
          };
        }

        this.carousel = new FoxTheme.Carousel(this.slideContainer, options);

        if (this.carousel) {
          this.carousel.init();
          this.calcNavButtonsPosition();
        }
      } catch (error) {
        console.error('Error initializing carousel:', error);
        this.destroyCarousel();
      }
    }
  }

  handleCarousel() {
    const containerEl = this.container;
    this.columns = containerEl.dataset.columns;
    this.enableSliderMobile = containerEl.dataset.enableSliderMobile === 'true';

    if (FoxTheme.config.mqlMobile) {
      this.enableSliderMobile ? this.initCarousel() : this.destroyCarousel();
    } else {
      this.initCarousel();
    }
  }

  destroyCarousel() {
    if (!this.wrapper?.innerHTML.trim().length) {
      this.classList.add('hidden');
      return;
    }
    this.container?.classList.remove(this.classes.swiper);
    this.wrapper?.classList.remove(this.classes.swiperWrapper);
    this.wrapper?.classList.add(this.classes.grid);

    if (typeof this.sliderInstance !== 'object') return;
    this.carousel.slider.destroy();
    this.carousel = false;
  }

  calcNavButtonsPosition() {
    if (!this.dataset.calcButtonPosition === 'true') return;
    const firstMedia = this.querySelector('.product-card__image-wrapper');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get container() {
    return this.querySelector('.cart-drawer-products-recommendation__container');
  }

  get wrapper() {
    return this.querySelector('.cart-drawer-products-recommendation__wrapper');
  }
}
defineCustomElement('cart-drawer-products-recommendation', CartDrawerProductsRecommendation);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }
}
defineCustomElement('main-cart', MainCart);

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };
    this.goal = Number(this.dataset.minimumAmount) * Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;
  }

  connectedCallback() {
    this.updateShippingGloal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:updated', (event) => {
      this.updateShippingGloal(event.detail.cart.items_subtotal_price);
    });
  }

  updateShippingGloal(amount) {
    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      this.querySelector(this.selectors.leftToSpend).innerHTML = FoxTheme.Currency.formatMoney(
        spend,
        this.money_format
      );
      this.classList.remove('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }
  }
}
defineCustomElement('free-shipping-goal', FreeShippingGoal);

window.FoxKitAddToCart = async (payload) => {
  if (!payload?.properties?.['_FoxKit offer']) return;

  const cartJson = await (
    await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig(),
    })
  ).json();
  cartJson['sections'] = payload['sections'];
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

  document.dispatchEvent(
    new CustomEvent('product-ajax:added', {
      detail: {
        product: payload,
      },
    })
  );
};
